import { userError } from "@/lib/userError";
import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Cpu, Mic, Mail, Calendar, Zap, ShieldCheck, Lock, Users, UserPlus, Copy, Brain, Radar, Play, CheckCircle2, CircleDashed, AlertTriangle, ArrowDown, HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createInvitation, diagnoseIntegration, getAuthStatus, getIntegrations, getOrganizationUsers, getMemory, createMemory, updateMemory, deleteMemory, getMonitors, createMonitor, updateMonitor, runMonitor } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { PageHeader, LoadingCards, ErrorState } from "@/components/Common";
import { cn } from "@/lib/utils";
import { MemoryList } from "@/components/MemoryList";
import { AssistantPreferences } from "@/components/AssistantPreferences";
import { fiRelative } from "@/lib/constants";
import { integrationFreshness, useFreshnessClock } from "@/lib/sourceFreshness";

const KIND_ICON = { llm: Cpu, orchestration: Zap, voice: Mic, email: Mail, calendar: Calendar, search: Radar };

const READY_STATUSES = new Set(["connected"]);

const SETUP_HELP = {
  "int-openai": {
    owner: "Ei toimenpiteitä",
    steps: ["OpenAI on valinnainen varamalli.", "Sitä ei tarvita, kun Relevance AI toimii pääpalveluna."],
  },
  "int-relevance": {
    owner: "Ylläpitäjä yhdistää",
    steps: ["Ylläpitäjä lisää projektin, agentin ja API-avaimen palvelimelle.", "Salaista avainta ei koskaan tallenneta selaimeen tai näytetä muille käyttäjille."],
  },
  "int-elevenlabs": {
    owner: "Ylläpitäjä yhdistää",
    steps: ["Ylläpitäjä lisää ääniavaimen ja valitun Voice ID:n palvelimelle.", "Käyttäjä testaa tämän jälkeen äänen omalla puhelimellaan."],
  },
  "int-gmail": {
    owner: "Käyttäjä hyväksyy",
    steps: ["Käyttäjä kirjautuu omaan Google-tiliinsä ja hyväksyy pyydetyn lukuoikeuden.", "Salasanaa ei anneta ACAI:lle. Viestejä ei lähetetä ilman erillistä hyväksyntää."],
  },
  "int-gcal": {
    owner: "Käyttäjä hyväksyy",
    steps: ["Käyttäjä valitsee oman Google-kalenterinsa ja hyväksyy yhteyden.", "Jokainen lisäys, muutos ja poisto pysyy erillisen hyväksynnän takana."],
  },
  "int-brave-search": {
    owner: "Ylläpitäjä yhdistää",
    steps: ["Ylläpitäjä lisää Brave Search -avaimen palvelimelle.", "Haku on vain luku -muotoinen eikä suorita verkkosivujen sisältämiä ohjeita."],
  },
};

function integrationReadiness(integration, now) {
  if (!integration) return "pending";
  if (integration.status === "error") return "attention";
  if (integration.status === "connected" && ["int-gmail", "int-gcal"].includes(integration.id) && integrationFreshness(integration, now) !== "fresh") return "attention";
  if (integration.status === "configured") return "attention";
  return READY_STATUSES.has(integration.status) ? "ready" : "pending";
}

export default function Settings() {
  const { data, loading, error, refetch } = useData(getIntegrations, [], ["integrations"]);
  const freshnessNow = useFreshnessClock();
  const [account, setAccount] = useState(null);
  const [accountChecked, setAccountChecked] = useState(false);
  const [users, setUsers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviting, setInviting] = useState(false);
  const { data: memoryData, loading: memoryLoading, refreshing: memoryRefreshing, error: memoryError, refetch: refreshMemory, setData: setMemoryData } = useData(getMemory, [], ["memory"]);
  const [memoryText, setMemoryText] = useState("");
  const [memoryCategory, setMemoryCategory] = useState("preference");
  const [editingMemory, setEditingMemory] = useState(null);
  const [memorySaving, setMemorySaving] = useState(false);
  const { data: monitorData, refetch: refreshMonitors } = useData(getMonitors, [], ["monitors"]);
  const monitors = monitorData || [];
  const [monitorKind, setMonitorKind] = useState("email_summary");
  const [diagnostics, setDiagnostics] = useState({});
  const [diagnosingId, setDiagnosingId] = useState("");

  const integrationMap = Object.fromEntries((data || []).map((integration) => [integration.id, integration]));
  const readinessItems = [
    { id: "account", label: "Käyttäjätili", detail: "Henkilökohtainen tietotila", state: !accountChecked ? "checking" : account ? "ready" : "attention", required: true },
    { id: "assistant", label: "Assistentti", detail: "Vastaukset ja toimintojen tulkinta", state: integrationReadiness(integrationMap["int-relevance"]), required: true },
    { id: "email", label: "Sähköposti", detail: "Omat viestit ja vastausluonnokset", state: integrationReadiness(integrationMap["int-gmail"], freshnessNow), required: true },
    { id: "calendar", label: "Kalenteri", detail: "Omat tapahtumat ja hyväksyttävät muutokset", state: integrationReadiness(integrationMap["int-gcal"], freshnessNow), required: true },
    { id: "voice", label: "Puheääni", detail: "Valinnainen puhuttu vastaus", state: integrationReadiness(integrationMap["int-elevenlabs"]), required: false },
    { id: "web", label: "Turvallinen verkkohaku", detail: "Valinnainen rajattu ajankohtaishaku", state: integrationReadiness(integrationMap["int-brave-search"]), required: false },
  ];
  const requiredReady = readinessItems.filter((item) => item.required).every((item) => item.state === "ready");
  const readinessChecking = readinessItems.some((item) => item.state === "checking");
  const requiredItems = readinessItems.filter((item) => item.required);
  const readyRequiredCount = requiredItems.filter((item) => item.state === "ready").length;
  const nextRequiredItem = requiredItems.find((item) => item.state !== "ready" && item.state !== "checking");
  const nextIntegrationId = {
    assistant: "int-relevance",
    email: "int-gmail",
    calendar: "int-gcal",
  }[nextRequiredItem?.id];

  const showNextIntegration = () => {
    if (!nextIntegrationId) return;
    document.querySelector(`[data-testid="integration-${nextIntegrationId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  useEffect(() => {
    let active = true;
    getAuthStatus().then((status) => {
      if (!active) return;
      setAccount(status.user);
      if (status.user?.role === "owner") {
        getOrganizationUsers().then((items) => active && setUsers(items)).catch(() => {});
      }
    }).catch(() => {
      if (active) setAccount(null);
    }).finally(() => {
      if (active) setAccountChecked(true);
    });
    return () => { active = false; };
  }, []);

  const invitePartner = async (event) => {
    event.preventDefault();
    setInviting(true);
    setInviteLink("");
    try {
      const invitation = await createInvitation(inviteEmail);
      const link = `${window.location.origin}/accept-invite?token=${encodeURIComponent(invitation.invite_token)}`;
      setInviteLink(link);
      setInviteEmail("");
      toast.success("Kutsulinkki luotu. Se on voimassa 7 päivää.");
    } catch (requestError) {
      toast.error(userError(requestError, "Kutsun luominen epäonnistui."));
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Kutsulinkki kopioitu.");
    } catch {
      toast.error("Kopiointi ei onnistunut. Valitse linkki ja kopioi se käsin.");
    }
  };

  const saveMemory = async (event) => {
    event.preventDefault();
    if (memorySaving || memoryLoading || memoryRefreshing || memoryError) return;
    setMemorySaving(true);
    try {
      if (editingMemory) await updateMemory(editingMemory, { content: memoryText, category: memoryCategory });
      else await createMemory({ content: memoryText, category: memoryCategory });
      setEditingMemory(null);
      setMemoryText("");
      const refreshed = await refreshMemory();
      if (refreshed === undefined) toast.warning("Muisto tallennettiin, mutta muistilistan päivitys epäonnistui. Päivitä lista ennen seuraavaa muutosta.");
      else toast.success("Muisto tallennettu.");
    } catch (requestError) {
      toast.error(userError(requestError, "Muiston tallentaminen epäonnistui."));
    } finally {
      setMemorySaving(false);
    }
  };

  const removeMemory = async (id) => {
    await deleteMemory(id);
    setMemoryData(current => Array.isArray(current) ? current.filter(memory => memory.id !== id) : current);
    if (editingMemory === id) {setEditingMemory(null); setMemoryText("");}
    const refreshed = await refreshMemory();
    if (refreshed === undefined) toast.warning("Muisto poistettiin, mutta muistilistan päivitys epäonnistui. Päivitä lista.");
    else toast.success("Muisto poistettu.");
  };

  const addMonitor = async (event) => {
    event.preventDefault();
    const names = {
      email_summary: "Seuraa tärkeitä sähköposteja",
      calendar_upcoming: "Seuraa tulevaa kalenteria",
      overdue_tasks: "Seuraa myöhästyneitä tehtäviä",
      reminder_due: "Seuraa erääntyviä muistutuksia",
    };
    try {
      await createMonitor({ name: names[monitorKind], kind: monitorKind, frequency_minutes: 1440 });
      await refreshMonitors();
      toast.success("Turvallinen seuranta lisätty.");
    } catch (requestError) {
      toast.error(userError(requestError, "Seurannan lisääminen epäonnistui."));
    }
  };

  const toggleMonitor = async (monitor) => {
    try {
      await updateMonitor(monitor.id, { enabled: !monitor.enabled });
      await refreshMonitors();
    } catch (requestError) {
      toast.error(userError(requestError, "Seurannan tilan muuttaminen epäonnistui."));
    }
  };

  const checkMonitor = async (monitor) => {
    try {
      const result = await runMonitor(monitor.id);
      toast.success(result.message);
      await refreshMonitors();
    } catch (requestError) {
      toast.error(userError(requestError, "Seurannan tarkistus epäonnistui."));
    }
  };

  const checkIntegration = async (integrationId) => {
    setDiagnosingId(integrationId);
    try {
      const result = await diagnoseIntegration(integrationId);
      setDiagnostics((current) => ({ ...current, [integrationId]: result }));
    } catch (requestError) {
      setDiagnostics((current) => ({
        ...current,
        [integrationId]: { state: "error", label: "Tarkistus epäonnistui", message: userError(requestError, "Palvelimen tilaa ei voitu tarkistaa.") },
      }));
    } finally {
      setDiagnosingId("");
    }
  };

  return (
    <div>
      <PageHeader icon={SettingsIcon} title="Oma assistenttini" subtitle="Toiminnot, omat tiedot ja palveluyhteydet." />
      <AssistantPreferences/>

      {loading ? <LoadingCards count={5} /> : error ? <ErrorState onRetry={refetch} /> : (
        <>
          <section className={cn("mb-5 rounded-2xl border p-4", requiredReady ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5")} data-testid="setup-readiness" aria-live="polite">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-foreground">Käyttöönoton tila</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {readinessChecking ? "Tarkistetaan vielä käyttäjätiliä ja palveluyhteyksiä." : requiredReady ? "Peruskäyttö on valmis. Valinnaisia toimintoja voi lisätä myöhemmin." : "Peruskäytöstä puuttuu vielä vähintään yksi välttämätön yhteys."}
                </p>
              </div>
              <span className={cn("shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold", requiredReady ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-400" : "border-amber-500/35 bg-amber-500/10 text-amber-400")}>
                {readinessChecking ? "Tarkistetaan" : requiredReady ? "Peruskäyttö valmis" : "Kesken"}
              </span>
            </div>
            <div className="mt-4 divide-y divide-border">
              {readinessItems.map((item) => {
                const StateIcon = item.state === "ready" ? CheckCircle2 : item.state === "attention" ? AlertTriangle : CircleDashed;
                const stateLabel = item.state === "ready" ? "Valmis" : item.state === "attention" ? "Tarkista" : item.state === "checking" ? "Tarkistetaan" : item.required ? "Kesken" : "Valinnainen";
                return (
                  <div key={item.id} className="flex items-start gap-2.5 py-3" data-testid={`readiness-${item.id}`}>
                    <StateIcon className={cn("mt-0.5 h-4 w-4 shrink-0", item.state === "ready" ? "text-emerald-400" : item.state === "attention" ? "text-amber-400" : "text-muted-foreground")} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">{item.label}</p>
                        <span className="text-xs text-muted-foreground">{stateLabel}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {!readinessChecking && !requiredReady && nextRequiredItem && (
              <div className="mt-3 border-t border-border pt-4" data-testid="setup-next-step">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-semibold text-foreground">Seuraava vaihe: {nextRequiredItem.label}</span>
                  <span className="shrink-0 text-muted-foreground">{readyRequiredCount}/{requiredItems.length} valmiina</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(readyRequiredCount / requiredItems.length) * 100}%` }} />
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {nextRequiredItem.state === "attention"
                    ? "Yhteys on lisätty, mutta sen toiminta täytyy vielä tarkistaa."
                    : `Yhdistä ${nextRequiredItem.label.toLowerCase()}, jotta peruskäyttö etenee.`}
                </p>
                {nextIntegrationId && (
                  <button type="button" onClick={showNextIntegration} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/15">
                    Näytä oikea kohta <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </section>

          <div className="space-y-3" data-testid="integrations-list">
          {data.map((i) => {
            const Icon = KIND_ICON[i.kind] || Cpu;
            const setupHelp = SETUP_HELP[i.id];
            const diagnostic = diagnostics[i.id];
            const connected = i.status === "connected";
            const configured = i.status === "configured";
            const failed = i.status === "error";
            const syncState = integrationFreshness(i, freshnessNow);
            const stale = connected && ["int-gmail", "int-gcal"].includes(i.id) && syncState !== "fresh";
            const statusLabel = failed ? "Virhe" : stale ? syncState === "stale" ? "Päivitys vanha" : "Päivitys epävarma" : connected ? "Toimii" : configured ? "Yhdistetty, ei testattu" : "Ei yhdistetty";
            return (
              <div key={i.id} className="rounded-2xl border border-border bg-card p-4 fade-up" data-testid={`integration-${i.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", connected && !stale ? "bg-primary/15 text-primary" : configured || stale ? "bg-amber-500/10 text-amber-400" : failed ? "bg-rose-500/10 text-rose-400" : "bg-muted text-muted-foreground")}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{i.id==='int-gmail'?(i.direct_arrival?'Oman Gmail-yhteyden lisätiedot':'Aiempi sähköpostiyhteys'):i.provider}</p>
                      <p className="text-xs text-muted-foreground">{i.id==='int-gmail'&&!i.direct_arrival?'Aiempi tuontiyhteys. Oman Google-tilin automaattinen saapuminen hallitaan ylempänä.':i.description}</p>
                    </div>
                  </div>
                  <span className={cn("shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold", connected && !stale ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-400" : configured || stale ? "border-amber-500/35 bg-amber-500/10 text-amber-400" : failed ? "border-rose-500/35 bg-rose-500/10 text-rose-400" : "border-border bg-muted text-muted-foreground")}>
                    {statusLabel}
                  </span>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground border-t border-border pt-2">
                  <Lock className="h-3 w-3" /> {i.docs}
                </p>
                {i.last_sync && <p className="mt-1 text-[11px] text-muted-foreground">Viimeksi päivitetty {fiRelative(i.last_sync)}.</p>}
                <button
                  type="button"
                  data-testid={`integration-diagnostic-${i.id}`}
                  onClick={() => checkIntegration(i.id)}
                  disabled={diagnosingId === i.id}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/50 px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary/40 disabled:opacity-50"
                >
                  {diagnosingId === i.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {diagnosingId === i.id ? "Tarkistetaan…" : "Tarkista turvallisesti"}
                </button>
                {diagnostic && (
                  <div className={cn("mt-2 rounded-xl border p-3 text-xs", diagnostic.state === "configured" ? "border-emerald-500/25 bg-emerald-500/5" : diagnostic.state === "error" ? "border-rose-500/25 bg-rose-500/5" : "border-amber-500/25 bg-amber-500/5")} role="status" data-testid={`integration-diagnostic-result-${i.id}`}>
                    <p className="font-semibold text-foreground">{diagnostic.label}</p>
                    <p className="mt-1 text-muted-foreground">{diagnostic.message}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">Tarkistus ei ottanut yhteyttä ulkoiseen palveluun.</p>
                  </div>
                )}
                {setupHelp && (
                  <details className="group mt-3 border-t border-border pt-2" data-testid={`integration-help-${i.id}`}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-lg py-1 text-xs font-medium text-foreground [&::-webkit-details-marker]:hidden">
                      <span className="flex items-center gap-1.5"><HelpCircle className="h-3.5 w-3.5 text-primary" /> Miten tämä yhdistetään?</span>
                      <span className="text-[10px] text-muted-foreground group-open:hidden">Näytä</span>
                      <span className="hidden text-[10px] text-muted-foreground group-open:inline">Piilota</span>
                    </summary>
                    <div className="mt-2 rounded-xl border border-border bg-background/45 p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{setupHelp.owner}</p>
                      <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                        {setupHelp.steps.map((step, index) => <li key={step} className="flex gap-2"><span className="text-foreground">{index + 1}.</span><span>{step}</span></li>)}
                      </ol>
                    </div>
                  </details>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}

      {/* Safety rules */}
      <section className="mt-6 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4" data-testid="safety-rules">
        <h2 className="font-heading font-bold text-foreground flex items-center gap-2 mb-3"><ShieldCheck className="h-5 w-5 text-primary" /> Turvasäännöt</h2>
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between rounded-xl border border-border bg-background/40 p-3"><div><p className="text-sm font-medium">Maksut estetty</p><p className="text-[11px] text-muted-foreground">Maksutoiminnot on poistettu käytöstä.</p></div><Lock className="h-5 w-5 text-accent" /></div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-background/40 p-3"><div><p className="text-sm font-medium">Puhelut vain sallituille</p><p className="text-[11px] text-muted-foreground">Vain ennalta hyväksytyt yhteystiedot.</p></div><Lock className="h-5 w-5 text-accent" /></div>
        </div>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex gap-2"><span className="text-emerald-500 mt-0.5">✓</span> Sähköpostin lähetys ja kalenterimuutokset vaativat aina hyväksyntäsi.</li>
          <li className="flex gap-2"><span className="text-emerald-500 mt-0.5">✓</span> Assistentti ei voi suorittaa omia ohjelmia tai tehdä mielivaltaisia verkkopyyntöjä.</li>
          <li className="flex gap-2"><span className="text-emerald-500 mt-0.5">✓</span> Verkkohaku käyttää kiinteää hakupalvelua ja käsittelee tulokset epäluotettavana datana.</li>
          <li className="flex gap-2"><span className="text-emerald-500 mt-0.5">✓</span> ACAI ei tallenna raakaääntä tietokantaan. Ääntä voidaan lähettää ElevenLabsille ja keskustelutekstiä Relevancelle käsittelyyn. Näiden palveluiden säilytys riippuu niiden asetuksista. Sähköpostien sisältämät ohjeet eivät anna assistentille toimintaoikeuksia.</li>
        </ul>
      </section>

      <details className="group mt-4 rounded-2xl border border-border bg-card p-4" data-testid="long-term-memory">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
          <span className="font-heading font-bold text-foreground flex items-center gap-2"><Brain className="h-5 w-5 text-primary" /> Pitkäkestoinen muisti</span>
          <span className="text-xs text-muted-foreground group-open:hidden">Avaa</span><span className="hidden text-xs text-muted-foreground group-open:inline">Sulje</span>
        </summary>
        <div className="mt-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground mb-4">Omat asiat -keskustelussa assistentti poimii automaattisesti omista viesteistäsi tärkeät pitkäkestoiset tiedot. Voit muokata ja poistaa niitä tässä. Muistia ei käytetä Yleistieto- tai yhteistyövastaustilassa. Mallille valitaan enintään 30 kysymykseen sopivaa muistoa 200 viimeisimmästä. Muistaminen vaatii toimivan malliyhteyden.</p>
        <p className="mb-4 text-xs text-muted-foreground">Poistettu tieto ei enää ole aktiivisessa muistissa. Estämme sen ja samankaltaisen tekstin automaattista palautumista, mutta eri sanoin kerrottu sama asia voi jäädä tunnistamatta. Voit tarvittaessa kytkeä automaattisen muistamisen pois. Käsin voit lisätä tiedon uudelleen.</p>
        <MemoryList memories={memoryData} loading={memoryLoading} busy={memorySaving} refreshing={memoryRefreshing} error={memoryError}
          onRefresh={refreshMemory} onDelete={removeMemory}
          onEdit={memory => {setEditingMemory(memory.id); setMemoryText(memory.content); setMemoryCategory(memory.category);}} />
        <form onSubmit={saveMemory} className="space-y-2">
          <fieldset disabled={memorySaving || memoryLoading || memoryRefreshing || Boolean(memoryError)} className="space-y-2">
          <select aria-label="Muiston ryhmä" value={memoryCategory} onChange={(event) => setMemoryCategory(event.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm">
            <option value="preference">Mieltymys</option><option value="person">Henkilö</option><option value="project">Projekti</option><option value="decision">Päätös</option><option value="routine">Rutiini</option><option value="other">Muu</option>
          </select>
          <div className="flex flex-col sm:flex-row gap-2">
            <input aria-label="Tallennettava muisto" value={memoryText} onChange={(event) => setMemoryText(event.target.value)} maxLength={2000} placeholder="Esim. Kasvujohtajan viikkopalaveri on maanantaisin" className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm" required />
            <button type="submit" disabled={memorySaving} className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">{memorySaving ? "Tallennetaan…" : editingMemory ? "Tallenna muutokset" : "Tallenna"}</button>
            {editingMemory && <button type="button" disabled={memorySaving} onClick={() => {setEditingMemory(null); setMemoryText("");}}>Peru muokkaus</button>}
          </div>
          </fieldset>
        </form>
        </div>
      </details>

      <details className="group mt-4 rounded-2xl border border-border bg-card p-4" data-testid="autonomous-monitors">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
          <span className="font-heading font-bold text-foreground flex items-center gap-2"><Radar className="h-5 w-5 text-primary" /> Itsenäiset seurannat</span>
          <span className="text-xs text-muted-foreground group-open:hidden">Avaa</span><span className="hidden text-xs text-muted-foreground group-open:inline">Sulje</span>
        </summary>
        <div className="mt-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground mb-4">Seurannat lukevat vain omaa dataasi ja raportoivat. Ne eivät lähetä, soita, maksa tai muuta kalenteria.</p>
        <div className="space-y-2 mb-4">
          {monitors.length === 0 && <p className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground">Ei aktiivisia seurantoja.</p>}
          {monitors.map((monitor) => (
            <div key={monitor.id} className="rounded-xl border border-border bg-background/40 p-3">
              <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-medium">{monitor.name}</p><p className="text-[11px] text-muted-foreground">{monitor.last_result || "Ei vielä tulosta"}</p></div><button type="button" onClick={() => toggleMonitor(monitor)} aria-pressed={monitor.enabled} className="rounded-full border border-border px-3 py-1 text-[11px]">{monitor.enabled ? "Päällä" : "Pois"}</button></div>
              {monitor.enabled && <button type="button" onClick={() => checkMonitor(monitor)} className="mt-2 inline-flex items-center gap-1 text-xs text-primary"><Play className="h-3 w-3" /> Tarkista nyt</button>}
            </div>
          ))}
        </div>
        <form onSubmit={addMonitor} className="flex flex-col sm:flex-row gap-2">
          <select aria-label="Seurannan kohde" value={monitorKind} onChange={(event) => setMonitorKind(event.target.value)} className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm">
            <option value="email_summary">Tärkeät sähköpostit</option><option value="calendar_upcoming">Tuleva kalenteri</option><option value="overdue_tasks">Myöhästyneet tehtävät</option><option value="reminder_due">Erääntyvät muistutukset</option>
          </select>
          <button type="submit" className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">Lisää seuranta</button>
        </form>
        </div>
      </details>

      {account?.role === "owner" && (
        <details className="group mt-4 rounded-2xl border border-border bg-card p-4" data-testid="organization-users">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
            <span className="font-heading font-bold text-foreground flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Yrityksen käyttäjät</span>
            <span className="text-xs text-muted-foreground group-open:hidden">Avaa</span><span className="hidden text-xs text-muted-foreground group-open:inline">Sulje</span>
          </summary>
          <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground mb-4">Kumppani liittyy vain henkilökohtaisella, seitsemän päivää voimassa olevalla kutsulinkillä.</p>
          <div className="space-y-2 mb-4">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/40 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{user.email || "Pääkäyttäjä"}</p>
                </div>
                <span className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground">
                  {user.role === "owner" ? "Pääkäyttäjä" : "Kumppani"}
                </span>
              </div>
            ))}
          </div>
          <form onSubmit={invitePartner} className="space-y-2">
            <label htmlFor="invite-email" className="text-xs text-muted-foreground">Kumppanin sähköposti</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="kumppani@yritys.fi"
                className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary"
                required
              />
              <button type="submit" disabled={inviting} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                <UserPlus className="h-4 w-4" /> {inviting ? "Luodaan…" : "Luo kutsu"}
              </button>
            </div>
          </form>
          {inviteLink && (
            <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
              <p className="text-xs text-muted-foreground mb-2">Jaa tämä linkki vain oikealle henkilölle. Linkki näkyy tässä vain nyt.</p>
              <div className="flex items-center gap-2">
                <input className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs" value={inviteLink} readOnly aria-label="Kutsulinkki" />
                <button type="button" onClick={copyInvite} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border" aria-label="Kopioi kutsulinkki">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          </div>
        </details>
      )}

    </div>
  );
}
